// Derivar a otra financiera (REQ-157): a dónde sigue la venta y qué dice cada error.
// Con extras el silo deja la venta activa para cobrarlos de contado; sin extras la pausa.
import { ApiError } from '@/api/errors';
import { lostReservationOf, PHONE_EXPIRED_MESSAGE } from './phoneReservation';
import { lineErrorMessage } from './lineErrors';
import {
    CREDIT_IN_PROGRESS_OTHER_STORE_CODE,
    CREDIT_IN_PROGRESS_OTHER_STORE_MESSAGE,
} from './cashOnlyPayment';
import { toExternalProvider } from '@/api/services/retail-wizard/externalProviderMapper';
import type { FinancierOption, WizardSession, WizardStepSlug } from './types';

type ReferralShape = Pick<WizardSession, 'state' | 'paymentMethodKind'>;

/** Venta derivada que sigue activa: falta cobrar sus extras de contado. */
export function isReferredWithExtras(session: ReferralShape): boolean {
    return session.paymentMethodKind === 'EXTERNAL' && session.state === 'ACTIVE';
}

/** Venta derivada sin extras: el silo la pausa en espera de la financiera, sin cobro aquí. */
export function isPausedForReferral(session: Pick<WizardSession, 'state'>): boolean {
    return session.state === 'PAUSED_EXTERNAL_REFERRAL';
}

/** Nombres de respaldo por si el catálogo de financieras no trae la de la venta. */
const PROVIDER_NAME: Readonly<Partial<Record<string, string>>> = {
    CREDIYA: 'CrediYa',
    PAYJOY: 'PayJoy',
};

/** Nombre legible de la financiera externa: del catálogo, del mapa de respaldo o el código. */
export function externalProviderName(
    provider: string | undefined,
    financiers: readonly Pick<FinancierOption, 'code' | 'name'>[] = [],
): string {
    if (!provider) return 'otra financiera';
    const fromCatalog = financiers.find((f) => toExternalProvider(f.code) === provider)?.name;
    return fromCatalog ?? PROVIDER_NAME[provider] ?? provider;
}

/** Tras derivar: activa va al cobro de los extras; pausada (o sin respuesta) a la Bienvenida. */
export function referralNextStep(
    session: Pick<WizardSession, 'state'> | null,
): Extract<WizardStepSlug, 'payment' | 'welcome'> {
    return session?.state === 'ACTIVE' ? 'payment' : 'welcome';
}

const REFERRAL_MESSAGE_BY_CODE: Record<string, string> = {
    PAID_SALE_LOCKED:
        'La venta ya tiene un cobro registrado: ya no puedes pasarla a otra financiera.',
    INVALID_SESSION_STATE: 'Esta venta ya no está activa. Vuelve al portal e inicia una nueva.',
    CREDIT_NOT_PENDING:
        'El crédito de SureKeep de esta venta ya se activó: ya no puedes pasarla a otra financiera.',
    EXTERNAL_INTEGRATION_ERROR:
        'El sistema de crédito no respondió y la venta quedó igual. Espera unos segundos y vuelve a intentar.',
    VALIDATION_ERROR:
        'No se pudo pasar la venta a esa financiera. Elige otra financiera o cóbrala de contado.',
    [CREDIT_IN_PROGRESS_OTHER_STORE_CODE]: CREDIT_IN_PROGRESS_OTHER_STORE_MESSAGE,
};

const REFERRAL_RETRY_MESSAGE = 'No se pudo derivar la venta. Vuelve a intentar.';
const REFERRAL_REJECTED_MESSAGE = 'No se pudo derivar la venta. Avisa a tu gerente.';

const REFERRAL_RELOAD_CODES = new Set([
    'SESSION_CONCURRENT_UPDATE',
    'CREDIT_NOT_PENDING',
    CREDIT_IN_PROGRESS_OTHER_STORE_CODE,
]);

export interface ReferralFailure {
    message: string;
    /** Releer la venta antes de que el vendedor decida otra cosa. */
    reload: boolean;
}

/** Solo un 5xx o un fallo de red invitan a reintentar; un 4xx volvería a fallar igual. */
function fallbackMessage(error: unknown): string {
    return error instanceof ApiError && error.httpStatus < 500
        ? REFERRAL_REJECTED_MESSAGE
        : REFERRAL_RETRY_MESSAGE;
}

/** Mensaje y recarga por cada code del derivar. */
export function referralFailure(error: unknown): ReferralFailure {
    const code = error instanceof ApiError ? error.code : undefined;
    const message =
        (code !== undefined ? REFERRAL_MESSAGE_BY_CODE[code] : undefined) ??
        lineErrorMessage(error) ??
        fallbackMessage(error);
    return { message, reload: code !== undefined && REFERRAL_RELOAD_CODES.has(code) };
}

export interface PaymentMethodFailure {
    /** Sin mensaje, el paso al que se regresa avisa por su cuenta. */
    message?: string;
    step: Extract<WizardStepSlug, 'product' | 'build-sale'>;
}

/** Salida propia de un error al elegir la forma de pago; `undefined` si no la tiene. */
export function paymentMethodFailure(
    error: unknown,
    session?: WizardSession,
    now: number = Date.now(),
): PaymentMethodFailure | undefined {
    const lost = lostReservationOf(error, session, now);
    if (lost === 'phone') return { message: PHONE_EXPIRED_MESSAGE, step: 'product' };
    if (lost === 'extra') return { step: 'build-sale' };
    return undefined;
}
